import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Snackbar,
	Tooltip,
	Typography
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
	useDeleteTalentSelection,
	useRevokeTalentPublicLink,
	useTalentSelectionDetail
} from "../../hooks/useAnonymizedTalents";
import { TalentPublicLink } from "../../models/TalentSelection";
import { zoullooColors } from "../../theme";

/**
 * Detail page of one saved selection. Lists every per-talent public link with:
 *   - Talent name (CSM is allowed to see it — they own the data)
 *   - Status (active / revoked / expired)
 *   - View count and last view time
 *   - Actions: copy URL / revoke link
 *   - Header action: delete the entire selection
 */
const TalentSelectionDetail: React.FC = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const selectionId = id ? Number(id) : null;

	const {
		data: selection,
		isLoading,
		isError
	} = useTalentSelectionDetail(selectionId);
	const revokeLink = useRevokeTalentPublicLink();
	const deleteSelection = useDeleteTalentSelection();

	const [toast, setToast] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [revokeDialogLinkId, setRevokeDialogLinkId] = useState<number | null>(null);

	const publicBaseUrl = useMemo(() => {
		// Reuse REACT_APP_FRONTEND_URL (already configured per env). Fall back to
		// the browser's current origin if it's missing — covers local dev too.
		const fromEnv =
			process.env.REACT_APP_FRONTEND_URL ?? window.location.origin;
		return fromEnv.replace(/\/$/, "");
	}, []);

	// Build the per-talent public URL, optionally pinned to a given UI language.
	// The public page reads `?lang=` and switches i18n on mount so the prospect
	// lands directly in the right language.
	const buildPublicUrl = (token: string, lang?: "fr" | "en") => {
		const base = `${publicBaseUrl}/p/talents/${token}`;
		return lang ? `${base}?lang=${lang}` : base;
	};

	const handleCopy = async (token: string, lang?: "fr" | "en") => {
		const url = buildPublicUrl(token, lang);
		try {
			await navigator.clipboard.writeText(url);
			// Single i18n key with the language interpolated, instead of three
			// hardcoded conditionals. When no lang is passed we fall back to the
			// language-agnostic toast.
			setToast(
				lang
					? t("anonymizedTalent.csmDetailToastCopiedLang", {
							lang: lang.toUpperCase()
						})
					: t("anonymizedTalent.csmDetailToastCopied")
			);
		} catch {
			setToast(t("anonymizedTalent.csmDetailToastClipboardError"));
		}
	};

	const handleRevoke = async (linkId: number) => {
		if (!selectionId) {
			return;
		}
		await revokeLink.mutateAsync({ selectionId, linkId });
		setToast(t("anonymizedTalent.csmDetailToastRevoked"));
	};

	const handleRevokeConfirmed = async () => {
		if (revokeDialogLinkId === null) {
			return;
		}
		const linkId = revokeDialogLinkId;
		setRevokeDialogLinkId(null);
		await handleRevoke(linkId);
	};

	const handleDeleteSelectionConfirmed = async () => {
		if (!selectionId) {
			return;
		}
		setDeleteDialogOpen(false);
		await deleteSelection.mutateAsync(selectionId);
		navigate("/talent-selections");
	};

	if (isLoading) {
		return (
			<Container maxWidth="lg" sx={{ py: 4 }}>
				<Box display="flex" justifyContent="center" sx={{ py: 8 }}>
					<CircularProgress />
				</Box>
			</Container>
		);
	}

	if (isError || !selection) {
		return (
			<Container maxWidth="lg" sx={{ py: 4 }}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => navigate("/talent-selections")}
					sx={{ mb: 2 }}
				>
					{t("anonymizedTalent.csmDetailBack")}
				</Button>
				<Box
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: zoullooColors.error.alpha.lighter,
						border: `1px solid ${zoullooColors.error.light}`,
						borderRadius: 2,
						color: zoullooColors.error.dark
					}}
				>
					{t("anonymizedTalent.csmDetailNotFound")}
				</Box>
			</Container>
		);
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Button
				startIcon={<ArrowBackIcon />}
				onClick={() => navigate("/talent-selections")}
				sx={{ mb: 2 }}
			>
				{t("anonymizedTalent.csmDetailBack")}
			</Button>

			<Box
				sx={{
					mb: 4,
					display: "flex",
					justifyContent: "space-between",
					alignItems: { xs: "flex-start", md: "center" },
					flexDirection: { xs: "column", md: "row" },
					gap: 2
				}}
			>
				<Box>
					<Typography variant="h2" sx={{ color: zoullooColors.secondary.dark }}>
						{selection.title}
					</Typography>
					<Typography sx={{ color: zoullooColors.text.secondary, fontSize: 13 }}>
						{t("anonymizedTalent.csmDetailCreatedAt", {
							date: new Date(selection.createdAt).toLocaleDateString(
								i18n.language,
								{
									day: "2-digit",
									month: "long",
									year: "numeric"
								}
							),
							count: selection.links.length
						})}
					</Typography>
				</Box>
				<Button
					startIcon={<DeleteOutlineIcon />}
					color="error"
					variant="outlined"
					onClick={() => setDeleteDialogOpen(true)}
				>
					{t("anonymizedTalent.csmDetailDelete")}
				</Button>
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
				{selection.links.length === 0 && (
					<Typography sx={{ color: zoullooColors.text.secondary, fontStyle: "italic" }}>
						{t("anonymizedTalent.csmDetailEmpty")}
					</Typography>
				)}
				{selection.links.map((link) => (
					<LinkRow
						key={link.id}
						link={link}
						publicUrl={buildPublicUrl(link.token)}
						onCopyFr={() => handleCopy(link.token, "fr")}
						onCopyEn={() => handleCopy(link.token, "en")}
						onRevoke={() => setRevokeDialogLinkId(link.id)}
					/>
				))}
			</Box>

			<Snackbar
				open={toast !== null}
				autoHideDuration={3000}
				onClose={() => setToast(null)}
				message={toast ?? ""}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			/>

			{/* MUI confirmation dialog — replaces the native window.confirm() so the
			    label / buttons can be themed and translated. */}
			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				aria-labelledby="delete-selection-dialog-title"
				aria-describedby="delete-selection-dialog-description"
			>
				<DialogTitle id="delete-selection-dialog-title">
					{t("anonymizedTalent.csmDetailDelete")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-selection-dialog-description">
						{t("anonymizedTalent.csmDetailDeleteConfirm")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)}>
						{t("anonymizedTalent.csmDetailDeleteCancel")}
					</Button>
					<Button
						onClick={handleDeleteSelectionConfirmed}
						color="error"
						variant="contained"
						autoFocus
					>
						{t("anonymizedTalent.csmDetailDeleteConfirmCta")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Confirmation before revoking a single public link. */}
			<Dialog
				open={revokeDialogLinkId !== null}
				onClose={() => setRevokeDialogLinkId(null)}
				aria-labelledby="revoke-link-dialog-title"
				aria-describedby="revoke-link-dialog-description"
			>
				<DialogTitle id="revoke-link-dialog-title">
					{t("anonymizedTalent.csmDetailRevokeConfirmTitle")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="revoke-link-dialog-description">
						{t("anonymizedTalent.csmDetailRevokeConfirmMessage")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRevokeDialogLinkId(null)}>
						{t("anonymizedTalent.csmDetailDeleteCancel")}
					</Button>
					<Button
						onClick={handleRevokeConfirmed}
						color="error"
						variant="contained"
						autoFocus
					>
						{t("anonymizedTalent.csmDetailRevokeConfirmCta")}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

const LinkRow: React.FC<{
	link: TalentPublicLink;
	publicUrl: string;
	onCopyFr: () => void;
	onCopyEn: () => void;
	onRevoke: () => void;
}> = ({ link, publicUrl, onCopyFr, onCopyEn, onRevoke }) => {
	const { t, i18n } = useTranslation();
	const expires = new Date(link.expiresAt);
	const lastViewed = link.lastViewedAt ? new Date(link.lastViewedAt) : null;

	const statusChip = (() => {
		if (link.isRevoked) {
			return (
				<Chip
					size="small"
					label={t("anonymizedTalent.csmDetailStatusExpiredCsm")}
					sx={{
						backgroundColor: zoullooColors.grey[200],
						color: zoullooColors.text.secondary,
						fontWeight: 600
					}}
				/>
			);
		}
		if (!link.isActive) {
			return (
				<Chip
					size="small"
					label={t("anonymizedTalent.csmDetailStatusExpired")}
					sx={{
						backgroundColor: zoullooColors.grey[200],
						color: zoullooColors.text.secondary,
						fontWeight: 600
					}}
				/>
			);
		}
		return (
			<Chip
				size="small"
				label={t("anonymizedTalent.csmDetailStatusActive")}
				sx={{
					backgroundColor: zoullooColors.proposal.seashell,
					color: zoullooColors.primary.dark,
					fontWeight: 700
				}}
			/>
		);
	})();

	// Headline shown under the name: expertise + experience (translated), mirroring
	// the anonymized view the prospect sees — replaces the raw profile title.
	const expertiseExperience = [
		link.freelancerExpertiseLabel &&
			t(`Expertises.${link.freelancerExpertiseLabel}`, link.freelancerExpertiseLabel),
		link.freelancerExperienceLabel &&
			t(`Experiences.${link.freelancerExperienceLabel}`, link.freelancerExperienceLabel)
	]
		.filter(Boolean)
		.join(" - ");

	return (
		<Box
			sx={{
				p: 2,
				border: `1px solid ${zoullooColors.grey[200]}`,
				borderRadius: 2,
				backgroundColor: "white",
				display: "grid",
				gridTemplateColumns: { xs: "1fr", md: "1.5fr 1fr 1fr auto" },
				alignItems: "center",
				gap: 2
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Typography
					sx={{
						fontWeight: 700,
						color: zoullooColors.secondary.dark,
						fontSize: 14,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
				>
					{link.freelancerDisplayName || t("anonymizedTalent.defaultTitle")}
				</Typography>
				{expertiseExperience && (
					<Typography
						sx={{
							fontSize: 12,
							color: zoullooColors.text.secondary
						}}
					>
						{expertiseExperience}
					</Typography>
				)}
				<Tooltip title={publicUrl} placement="top">
					<Typography
						sx={{
							fontSize: 11,
							fontFamily: "monospace",
							color: zoullooColors.text.secondary,
							mt: 0.5,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap"
						}}
					>
						{publicUrl}
					</Typography>
				</Tooltip>
			</Box>
			<Box>
				{statusChip}
				<Typography sx={{ fontSize: 11, color: zoullooColors.text.secondary, mt: 0.5 }}>
					{t("anonymizedTalent.csmDetailExpiresOn", {
						date: expires.toLocaleDateString(i18n.language, {
							day: "2-digit",
							month: "2-digit",
							year: "numeric"
						})
					})}
				</Typography>
			</Box>
			<Box>
				<Typography
					sx={{
						fontSize: 16,
						fontWeight: 800,
						color: zoullooColors.secondary.dark
					}}
				>
					{t("anonymizedTalent.csmDetailViewsLabel", { count: link.viewCount })}
				</Typography>
				{lastViewed && (
					<Typography sx={{ fontSize: 11, color: zoullooColors.text.secondary }}>
						{t("anonymizedTalent.csmDetailLastView", {
							datetime: `${lastViewed.toLocaleDateString(i18n.language, {
								day: "2-digit",
								month: "2-digit"
							})} ${lastViewed.toLocaleTimeString(i18n.language, {
								hour: "2-digit",
								minute: "2-digit"
							})}`
						})}
					</Typography>
				)}
			</Box>
			<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
				<Tooltip title={t("anonymizedTalent.csmDetailCopyFrTooltip")}>
					<Button
						onClick={onCopyFr}
						size="small"
						variant="outlined"
						startIcon={<ContentCopyIcon fontSize="small" />}
						sx={{
							minWidth: 0,
							px: 1,
							fontSize: 11,
							fontWeight: 700,
							borderColor: zoullooColors.primary.main,
							color: zoullooColors.primary.main,
							"&:hover": {
								borderColor: zoullooColors.primary.dark,
								backgroundColor: zoullooColors.proposal.seashell
							}
						}}
					>
						FR
					</Button>
				</Tooltip>
				<Tooltip title={t("anonymizedTalent.csmDetailCopyEnTooltip")}>
					<Button
						onClick={onCopyEn}
						size="small"
						variant="outlined"
						startIcon={<ContentCopyIcon fontSize="small" />}
						sx={{
							minWidth: 0,
							px: 1,
							fontSize: 11,
							fontWeight: 700,
							borderColor: zoullooColors.secondary.main,
							color: zoullooColors.secondary.main,
							"&:hover": {
								borderColor: zoullooColors.secondary.dark,
								backgroundColor: zoullooColors.proposal.magnolia
							}
						}}
					>
						EN
					</Button>
				</Tooltip>
				{!link.isRevoked && link.isActive && (
					<Tooltip title={t("anonymizedTalent.csmDetailRevokeTooltip")}>
						<IconButton onClick={onRevoke} size="small" color="error">
							<BlockIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</Box>
		</Box>
	);
};

export default TalentSelectionDetail;
